import i18n from 'i18next';
import qs from 'qs';
import { convert } from 'utils/betslip/odd-formats-helper';
import {
  CYPRUS_COMPETITION_SELECTIONS_COUNT_ERROR_CODE,
  MODE_TYPES,
  REPLACE_EDIT_BET_TRANSLATIONS
} from 'utils/constants/betslip/bet-types';
import { ERROR_CODE_TIMEOUT_1800 } from 'utils/constants/dateInfo/time-numbers';
import { EAccount } from 'utils/constants/store/query-keys';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import {
  CommandNames,
  CommandSource
} from 'utils/constants/swarm/swarm-command-names';
import { SwarmSuccessMessages } from 'utils/constants/swarm/swarm-success-messages';
import { fireEvent } from 'utils/generic/event-bus';
import { showToastError } from 'utils/generic/show-toast-error';
import { appendCustomCode } from 'utils/other/customCode';
import RidGenerator from 'utils/swarm/rid-generator';
import { showToastSuccess } from 'utils/swarm/swarm-success-handler';
import { GetCurrencyConfigsResponse } from 'interfaces/account-settings';
import {
  BookBetParamsType,
  DoBetResponseType,
  TCreateBets,
  TV3FreebetParams,
  TV3FreebetResponse
} from 'interfaces/bet-data';
import { doBetProps, IEventsAndGameIds } from 'interfaces/betslip';
import { AccountParamsType, AppendCustomCodes } from 'interfaces/generic';
import {
  CommandObj,
  WebsocketEnhanced
} from 'interfaces/spring-websocket-interfaces';
import { EPlayerActivityCheckerActions } from 'newcomponents/Shared/account/PlayerActivityChecker/types';
import { connectingWS } from 'hooks/useSpringConnect';
import Store from 'store';
import {
  batchActions,
  removeFreeBetItem,
  setFreeBetSelectedEventId,
  setSelectedProfitBoostEventId,
  setShouldAddSelectionsToCyprusCompetitions
} from 'store/actions';
import { IBetslipRootStateInitialData } from 'store/reducers/betslip';
import QueryClient from 'stores/query/query-client';
import { deviceActionManager } from '../deviceactionmanager/DeviceActionManager';
import { EBetSuccessModalActions } from '../newelements/_Betslip/components/shared/Bets/types';

const { queryClient } = QueryClient;

// this is required for showing messages outside the betslip if it is closed
const getMessageContextBasedOnBetslipOpenState = (
  messageContext: HTMLElement | null | undefined
): HTMLElement | null | undefined => {
  const isBetslipOpen = Store.getState().betSlip?.betslipOpen;
  const isFloatingBetslipEnabled = SpringConfigs.IS_BETSLIP_FLOATING_ENABLED;

  if (
    !isFloatingBetslipEnabled ||
    (isFloatingBetslipEnabled && isBetslipOpen)
  ) {
    return messageContext;
  }

  return null;
};

/* eslint-disable */
export const betCommand = async (
  commandObj: any, //  command object must be of type any for generic purpose
  rid: string,
  callback: Function,
  updateCallback: Function | null,
  callbackError: Function | null
): Promise<void> => {
  /* eslint-enable */
  connectingWS.then((socket: WebsocketEnhanced) => {
    socket.sendCommand(
      commandObj,
      rid,
      callback,
      updateCallback,
      callbackError
    );
  });
};

const oddChangeHandlerCallbackWrapper = (
  response: DoBetResponseType,
  callback: Function | null,
  oddFormat: number,
  setAcceptBet?: Function,
  messageContext?: HTMLElement | null
): void => {
  callback?.(response);
  const { result, result_text, details, StatusCode, Data } = response;

  const isStatusCode = StatusCode === REPLACE_EDIT_BET_TRANSLATIONS[0];

  if (result === ERROR_CODE_TIMEOUT_1800 || isStatusCode) {
    const changedOdd = isStatusCode
      ? Data?.ErrorData
        ? {
            old_price: Data?.ErrorData?.OldPrice,
            new_price: Data?.ErrorData?.ActualPrice
          }
        : null
      : Array.isArray(details)
      ? {
          old_price: details[0].old_price,
          new_price: details[0].new_price
        }
      : null;

    if (setAcceptBet) {
      setAcceptBet(true);
    }

    if (!changedOdd) {
      return;
    }

    const oddsChangedText = i18n
      .t('swarm.OddsIsChanged')
      .replace('{oldPrice}', convert(changedOdd.old_price, oddFormat))
      .replace('{newPrice}', convert(changedOdd.new_price, oddFormat));

    showToastError(
      oddsChangedText,
      getMessageContextBasedOnBetslipOpenState(messageContext),
      result_text
    );
  }
};

type TDuBet = (doBetParams: {
  data: doBetProps;
  // we need this to pass some variables to appendCustomCode
  additionalInfo: { possibleWin?: number };
  callback: Function;
  callbackError: Function | null;
  setAcceptBet?: Function;
  messageContext?: HTMLElement | null;
  showToastMessageOnSuccess?: boolean;
}) => void;

// DON'T use this function directly in project, instead use useDoBetHandler : doBetHandler function
export const doBet: TDuBet = doBetParams => {
  const editBet = Store.getState().betSlip?.editBet;

  // @Todo add type for betslip do bet response
  const callback = (res: any) => {
    doBetParams?.callback(res);

    if (SpringConfigs.WRAPPER_APP && window.median) {
      deviceActionManager.vibrate('impactMedium');
    }
  };

  if (editBet?.active) {
    sendCreateBet({
      ...doBetParams,
      callback
    });
  } else {
    sendDoBet({
      ...doBetParams,
      callback
    });
  }
};

/*eslint no-magic-numbers:*/
// TODO check after betslip refactor
const sendDoBet: TDuBet = ({
  data,
  callback,
  callbackError,
  setAcceptBet,
  messageContext,
  showToastMessageOnSuccess,
  additionalInfo
}) => {
  const betslipStore: IBetslipRootStateInitialData = Store.getState()?.betSlip;
  const oddFormat = betslipStore.oddFormat;
  const editBet = betslipStore.editBet;
  const isBonusMoney = betslipStore.useBonusBalance;
  const isQuickBetEnabled = betslipStore.quickBetEnabled;
  const user = Store.getState().userData.user;
  const isFrenchFreeBetEnabled =
    Store.getState().betSlip.isFrenchFreeBetEnabled;

  const {
    bets,
    type,
    mode,
    sys_bet,
    bonus_id,
    predefined_multiple_id,
    source,
    each_way,
    booking_id,
    location_token
  } = data;

  const amount = editBet?.active ? editBet.amount : data.amount;

  const additional_amount = data.amount;
  const old_bet_id = editBet?.betId || null;

  const command = {
    command: CommandNames.DO_BET,
    params: {
      source,
      each_way,
      bonus_id,
      amount,
      bets,
      mode: mode || MODE_TYPES.ODD_NOT_CHANGE,
      odd_type: oddFormat,
      type,
      booking_id,
      is_bonus_money: !!isBonusMoney,
      ...(isFrenchFreeBetEnabled ? { is_free_bet_money: true } : {}),
      ...(location_token ? { location_token } : {})
    } as Record<string, any>,
    rid: RidGenerator.gForCommand()
  };

  if (sys_bet) {
    command.params.sys_bet = sys_bet;
  }

  if (predefined_multiple_id) {
    command.params.predefined_multiple_id = predefined_multiple_id;
  }

  if (editBet?.active) {
    command.params.additional_amount = additional_amount;
    command.params.old_bet_id = old_bet_id;
  }

  betCommand(
    command,
    '',
    (data: {
      result: string;
      details: {
        bet_id: number;
        k: number;
        is_superbet: boolean;
      };
    }) => {
      callback(data);
      fireEvent({ key: EPlayerActivityCheckerActions.checkPlayerActivity });

      const betslipStore: IBetslipRootStateInitialData =
        Store.getState().betSlip;

      const freeBetStore = betslipStore.freeBets;

      Store.dispatch(
        batchActions(
          setSelectedProfitBoostEventId(null),
          setFreeBetSelectedEventId(null)
        )
      );

      if (freeBetStore.selectedFreeBetId) {
        Store.dispatch(
          batchActions(removeFreeBetItem(freeBetStore.selectedFreeBetId))
        );
      }

      if (data && data.result === 'OK') {
        if (isQuickBetEnabled || showToastMessageOnSuccess) {
          if (data.details?.is_superbet) {
            showToastSuccess(
              i18n.t('betslip.superBetSuccess'),
              getMessageContextBasedOnBetslipOpenState(messageContext)
            );
          } else {
            showToastSuccess(
              i18n.t(`betslip.${SwarmSuccessMessages.DO_BET}`),
              getMessageContextBasedOnBetslipOpenState(messageContext)
            );
          }
        } else {
          fireEvent({
            key: EBetSuccessModalActions.doBetSuccess,
            payload: data.details?.is_superbet
              ? 'superBetSuccess'
              : `${SwarmSuccessMessages.DO_BET}`
          });
        }
      }

      const accountParams: AccountParamsType = queryClient.getQueryData([
        EAccount['AccountParams']
      ]);

      if (
        accountParams &&
        +accountParams[AppendCustomCodes.APPEND_CUSTOM_CODE_BETSLIP]
      ) {
        appendCustomCode(accountParams[AppendCustomCodes.CUSTOM_CODE_BETSLIP], {
          amount,
          currency: user?.currency,
          bet_id: data?.details?.bet_id,
          k: data?.details?.k,
          is_superbet: mode === -1,
          bet_type: type,
          name: user?.name,
          first_name: user?.first_name,
          last_name: user?.last_name,
          email: user?.email,
          username: user?.username,
          btag: user?.btag,
          userId: user?.id,
          phone: user?.phone,
          possible_win: additionalInfo.possibleWin
        });
      }
    },
    () => undefined,
    ({ data }: { data: DoBetResponseType }) => {
      if (data.result === CYPRUS_COMPETITION_SELECTIONS_COUNT_ERROR_CODE) {
        Store.dispatch(setShouldAddSelectionsToCyprusCompetitions(true));
      }

      oddChangeHandlerCallbackWrapper(
        data,
        callbackError,
        oddFormat,
        setAcceptBet,
        messageContext
      );
    }
  );
};

const sendCreateBet: TDuBet = ({
  data,
  callback,
  callbackError,
  setAcceptBet,
  messageContext,
  showToastMessageOnSuccess,
  additionalInfo
}) => {
  const oddFormat = Store.getState().betSlip.oddFormat;
  const editBet = Store.getState().betSlip.editBet;
  const user = Store.getState().userData.user;
  const isQuickBetEnabled = Store.getState().betSlip.quickBetEnabled;

  const isFrenchFreeBetEnabled =
    Store.getState().betSlip.isFrenchFreeBetEnabled;

  const { bets, type, mode, source, each_way, location_token } = data;

  const amount = editBet?.active ? editBet.amount : data.amount;

  const additional_amount = data.amount;
  const old_bet_id = editBet?.betId || null;

  const command: TCreateBets = {
    command: CommandNames.CREATE_BETS,
    params: {
      bets: [
        {
          Source: source,
          EachWay: each_way,
          Amount: amount,
          Events: bets.map(bet => ({
            SelectionId: bet.event_id,
            Coeficient: bet.price
          })),
          OddType: oddFormat,
          Type: type,
          AdditionalAmount: undefined,
          ...(isFrenchFreeBetEnabled ? { IsFreeBetMoney: true } : {})
        }
      ],
      ...(location_token ? { GeoLocationToken: location_token } : {})
    },
    rid: RidGenerator.gForCommand()
  };

  if (editBet?.active) {
    command.params.bets[0].AdditionalAmount = additional_amount;
    command.params.bets[0].OldBetId = old_bet_id;
  }

  betCommand(
    command,
    '',
    (data: {
      result: string;
      StatusCode: string;
      Data: Array<{
        bet_id: number;
        k: number;
        is_superbet?: boolean;
      }>;
    }) => {
      if (data.StatusCode === '0') {
        data.result = 'OK';

        if (isQuickBetEnabled || showToastMessageOnSuccess) {
          if (data.Data?.[0]?.is_superbet) {
            showToastSuccess(
              i18n.t('betslip.superBetSuccess'),
              getMessageContextBasedOnBetslipOpenState(messageContext)
            );
          } else {
            showToastSuccess(
              i18n.t(`betslip.${SwarmSuccessMessages.DO_BET}`),
              getMessageContextBasedOnBetslipOpenState(messageContext)
            );
          }
        } else {
          fireEvent({
            key: EBetSuccessModalActions.doBetSuccess,
            payload: data.Data?.[0]?.is_superbet
              ? 'superBetSuccess'
              : `${SwarmSuccessMessages.DO_BET}`
          });
        }
      }

      callback(data);
      fireEvent({ key: EPlayerActivityCheckerActions.checkPlayerActivity });
      const accountParams: AccountParamsType = queryClient.getQueryData([
        EAccount['AccountParams']
      ]);

      if (
        accountParams &&
        +accountParams[AppendCustomCodes.APPEND_CUSTOM_CODE_BETSLIP]
      ) {
        appendCustomCode(accountParams[AppendCustomCodes.CUSTOM_CODE_BETSLIP], {
          amount,
          currency: user?.currency,
          bet_id: data?.Data[0]?.bet_id,
          k: data?.Data[0]?.k,
          is_superbet: mode === -1,
          bet_type: type,
          name: user?.name,
          first_name: user?.first_name,
          last_name: user?.last_name,
          email: user?.email,
          username: user?.username,
          btag: user?.btag,
          userId: user?.id,
          phone: user?.phone,
          possible_win: additionalInfo.possibleWin
        });
      }
    },
    () => undefined,
    ({ data }: { data: DoBetResponseType }) => {
      oddChangeHandlerCallbackWrapper(
        data,
        callbackError,
        oddFormat,
        setAcceptBet,
        messageContext
      );
    }
  );
};

export const getV3Freebet = (
  params: TV3FreebetParams,
  callback: (data: TV3FreebetResponse) => void,
  callbackError: ((err?: Record<string, string>) => void) | null = null
): void => {
  const command = {
    command: CommandNames.GET_FREEBETS_FOR_BETSLIP_V3,
    params,
    rid: RidGenerator.gForCommand()
  };

  betCommand(command, '', callback, () => undefined, callbackError);
};

export const getCurrencyConfigs = async (
  currency: string,
  callback: (data: GetCurrencyConfigsResponse) => void,
  callbackError: Function | null
): Promise<void> => {
  const commandObj = {
    command: CommandNames.GET,
    rid: RidGenerator.gForCommand(),
    params: {
      source: CommandSource.CURRENCY_CONFIG,
      what: {
        currency: []
      },
      where: {
        currency: { name: currency }
      },
      subscribe: false
    }
  };

  connectingWS.then((socket: WebsocketEnhanced) =>
    socket.sendCommand(commandObj, '', callback, undefined, callbackError)
  );
};

export const bookBet = async (
  bets: BookBetParamsType,
  callback: Function,
  callbackError: Function
): Promise<void> => {
  const commandObj: CommandObj = {
    command: CommandNames.BOOK_BET,
    rid: RidGenerator.gForCommand(),
    params: {
      bets
    }
  };

  const tid = Number(
    qs.parse(window.location.search, { ignoreQueryPrefix: true }).tid
  );

  tid && (commandObj.params.cashdesk_id = tid);

  connectingWS.then((socket: WebsocketEnhanced) =>
    socket.sendCommand(commandObj, '', callback, undefined, callbackError)
  );
};

export const getEventsByBookingId = (
  bookId: string,
  callback: Function,
  callbackError: Function
): void => {
  const commandObj: CommandObj = {
    command: CommandNames.GET_EVENTS_BY_BOOKING_ID,
    rid: RidGenerator.gForCommand(),
    params: {
      booking_id: +bookId
    }
  };

  connectingWS.then((socket: WebsocketEnhanced) =>
    socket.sendCommand(commandObj, '', callback, undefined, callbackError)
  );
};

export const getEventsReadyForBetslip = (
  eventsAndGameIds: IEventsAndGameIds[] | null,
  callback: Function,
  callBackError?: Function,
  getOnlyEventIds?: boolean,
  gameIds?: number[],
  eventIds?: number[]
): void => {
  const games = gameIds
    ? gameIds
    : eventsAndGameIds
    ? eventsAndGameIds.map(entity => entity.gameId)
    : [];

  const events =
    gameIds && eventIds
      ? eventIds
      : eventsAndGameIds
      ? eventsAndGameIds.map(entity => entity.eventId)
      : [];

  const commandObj = {
    command: CommandNames.GET,
    rid: RidGenerator.gForCommand(),
    params: {
      source: CommandSource.BETTING,
      what: {
        ...(!getOnlyEventIds && {
          sport: ['alias', 'type'],
          region: ['name', 'alias'],
          competition: ['name', 'id'],
          game: ['id', 'type', 'team1_name', 'team2_name', 'start_ts', 'info'],
          market: ['name', 'type', 'extra_info']
        }),
        event: getOnlyEventIds
          ? ['id']
          : ['id', 'base', 'name', 'price', 'ew_allowed']
      },
      where: {
        game: {
          id: {
            '@in': games
          }
        },
        event: {
          id: {
            '@in': events
          }
        }
      }
    }
  };

  connectingWS.then((socket: WebsocketEnhanced) => {
    socket.sendCommand(commandObj, '', callback, undefined, callBackError);
  });
};

export const counterCounterOfferAnswer = (
  acceptOffer: boolean,
  betId: number | string,
  callback: Function,
  callbackError?: Function
): void => {
  const commandObj: CommandObj = {
    command: CommandNames.SUPER_BET_ANSWER,
    rid: RidGenerator.gForCommand(),
    params: {
      accept: acceptOffer,
      bet_id: betId
    }
  };

  connectingWS.then((socket: WebsocketEnhanced) =>
    socket.sendCommand(commandObj, '', callback, undefined, callbackError)
  );
};

export const checkBetStatus = (
  betId: number | string,
  callback: Function,
  callbackError?: Function
): void => {
  const commandObj: CommandObj = {
    command: CommandNames.CHECK_BET_STATUS,
    rid: RidGenerator.gForCommand(),
    params: {
      bet_id: betId
    }
  };

  connectingWS.then((socket: WebsocketEnhanced) =>
    socket.sendCommand(commandObj, '', callback, undefined, callbackError)
  );
};
